import { createContext, useContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import { pl } from './locales/pl'
import { en } from './locales/en'
import type { Language, Dictionary } from './types'

const dictionaries: Record<Language, Dictionary> = { pl, en }
const STORAGE_KEY = 'solidlabs-language'

interface LanguageContextValue {
  language: Language
  setLanguage: (lang: Language) => void
  t: Dictionary
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

/**
 * Detect initial language preference at mount time.
 * Priority:
 *   1. localStorage (user's previously-set choice)
 *   2. navigator.language ('pl*' → PL, else EN)
 *   3. 'pl' fallback
 */
function detectInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'pl'

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'pl' || stored === 'en') return stored
  } catch {
    // localStorage may be disabled (private mode, strict permissions)
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language.toLowerCase().startsWith('pl') ? 'pl' : 'en'
  }

  return 'pl'
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(detectInitialLanguage)

  // Sync browser-level metadata with current language.
  // Runs on mount and any time language changes.
  useEffect(() => {
    const dict = dictionaries[language]
    document.documentElement.lang = language
    document.title = dict.meta.title
    const descTag = document.querySelector('meta[name="description"]')
    if (descTag) descTag.setAttribute('content', dict.meta.description)
  }, [language])

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
    try {
      window.localStorage.setItem(STORAGE_KEY, lang)
    } catch {
      // localStorage may be disabled — switch still works in-memory.
    }
  }

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, t: dictionaries[language] }}
    >
      {children}
    </LanguageContext.Provider>
  )
}

/** Zwraca aktywny słownik. Użycie: `const t = useT(); t.nav.about` */
export function useT(): Dictionary {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useT must be used within LanguageProvider')
  return ctx.t
}

/** Zwraca aktualny język i setter — do przełącznika PL/EN. */
export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider')
  return { language: ctx.language, setLanguage: ctx.setLanguage }
}
